import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import type { JSONContent } from '@tiptap/core';
import classNames from 'classnames';

import FormWrapper from './components/FormWrapper';
import GridWrapper from './components/GridWrapper';
import LabelWrapper from './components/LabelWrapper';
import PopupElement from './components/PopupElement';
import RecommendationsForm from './components/RecommendationsForm';

import { computeFont } from '../utils/fonts';
import { defaultStructure as defaultRecommendationsStructure } from './utils/defaultStructure';

import type { SerializableNode } from '../serializer';
import type { Recommendation } from '../types';
import * as styles from './Recommendations.css';
import type { RecommendationsElementProps } from './Recommendations.types';
import { RecommendationsSerializer } from './Serializer';
import Serializer from './serializerV1/Serializer';

import { SwitchElement } from '..';

export function RecommendationsElement(props: RecommendationsElementProps) {
  const {
    element,
    children,
    attributes,
    className,
    onChange,
    onSelectAll,
    onDeselectAll,
    email: defaultEmail,
    showForm = false,
    isLive = false,
    selectedRecommendations = [],
    portalElement,
    onClickSubmit,
  } = props;

  const [email, setEmail] = useState('');
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  const {
    mobile_hide,
    desktop_hide,
    customAttributes,
    padding,
    gap,
    shadow,
    backgroundColor,
    nameColor,
    descriptionColor,
    data = [],
    fontFamily,
    buttonText,
    toggleBackgroundColor,
    toggleCheckedBackgroundColor,
    toggleThumbColor,
    buttonCustomBackgroundColor,
    buttonCustomTextColor,
    isPopupForm,
    containerBorderWidth,
    containerBorderRadius,
    sponsoredTextColor,
    containerBorderStyle,
    containerBorderColor,
    cardBorderWidth,
    cardBorderColor,
    cardPadding,
    cardBorderStyle,
    cardBorderRadius,
    inputPlaceholderText,
    inputTextColor,
    inputPlaceholderTextColor,
    selectAllTextColor,
    cardStructureV2,
    structureTiptapState,
    selectAllPadding,
  } = element.attrs || {};

  const selectedRecommendationIds = useMemo(
    () => selectedRecommendations.map((recommendation) => recommendation.id),
    [selectedRecommendations],
  );

  const handleChangeSelectAll = (checked: boolean) => {
    setIsSelectAllChecked(checked);

    if (checked) {
      onSelectAll?.(data);
    } else {
      onDeselectAll?.();
    }
  };

  const handleChange = (recommendation: Recommendation) => {
    onChange?.(recommendation);
  };

  const handleSubmit = () => {
    if (!onClickSubmit) {
      return;
    }

    if (onClickSubmit(defaultEmail || email)) {
      setEmail('');
      setIsSelectAllChecked(false);
    }
  };

  const formElement = (
    <RecommendationsForm
      defaultEmail={defaultEmail}
      isLive={isLive}
      isPopupForm={isPopupForm || false}
      portalElement={portalElement}
      email={email}
      setEmail={setEmail}
      handleSubmit={handleSubmit}
      inputPlaceholderText={inputPlaceholderText}
      attrs={element.attrs}
      buttonText={buttonText}
      selectedRecommendations={selectedRecommendations}
    />
  );

  const popupElement = (
    <PopupElement attrs={element.attrs} showForm={showForm} isLive={isLive}>
      {formElement}
    </PopupElement>
  );

  const selectAllSwitchId = `${element.attrs.id}-select-all-switch`;

  useEffect(() => {
    if (data.length === selectedRecommendationIds.length) {
      setIsSelectAllChecked(true);
    } else {
      setIsSelectAllChecked(false);
    }
  }, [selectedRecommendationIds]);

  return (
    <>
      <div
        {...customAttributes}
        className={classNames(
          className,
          {
            'mobile:hidden': mobile_hide,
            'desktop:hidden': desktop_hide,
          },
          styles.recommendationsElement,
          customAttributes?.className,
          'flex flex-col justify-between relative',
        )}
        style={
          {
            width: '100%',
            overflow: 'visible',
            padding: padding,
            fontFamily: computeFont(fontFamily) || 'inherit',
            borderWidth: containerBorderWidth,
            borderStyle: containerBorderStyle,
            borderColor: containerBorderColor,
            borderRadius: containerBorderRadius,
            '--name-text-color': nameColor,
            '--description-text-color': descriptionColor,
            '--button-custom-text-color': buttonCustomTextColor,
            '--button-custom-background-color': buttonCustomBackgroundColor,
            '--toggle-background-color': toggleBackgroundColor,
            '--toggle-checked-background-color': toggleCheckedBackgroundColor,
            '--select-all-text-color': selectAllTextColor,
            '--sponsored-text-color': sponsoredTextColor,
            '--input-text-color': inputTextColor,
            '--input-placeholder-text-color': inputPlaceholderTextColor,
          } as CSSProperties
        }
        {...attributes}
      >
        <FormWrapper gap={gap || 0}>
          <div
            className={classNames('flex items-center justify-end w-full', !isLive && 'pointer-events-none')}
            style={{ padding: selectAllPadding }}
          >
            <SwitchElement
              isLive={isLive}
              checked={isSelectAllChecked}
              element={{
                type: 'switch',
                attrs: {
                  id: selectAllSwitchId,
                  backgroundColor: toggleBackgroundColor,
                  backgroundColorSelect: toggleCheckedBackgroundColor,
                  thumbColor: toggleThumbColor || '#ffffff',
                  gap: '5px',
                  labelPlacement: 'start',
                },
                content: [{ id: '', text: '' }],
              }}
              onCheckedChange={handleChangeSelectAll}
            >
              <label className={classNames(styles.selectAllLabel)} htmlFor={selectAllSwitchId}>
                {isSelectAllChecked ? 'Deselect' : 'Select'} all
              </label>
            </SwitchElement>
          </div>

          <GridWrapper attrs={element.attrs}>
            {data?.map((recommendation) => {
              const isChecked = selectedRecommendationIds.includes(recommendation.id);

              if (cardStructureV2) {
                return (
                  <div key={recommendation.id}>
                    <LabelWrapper isLive={isLive} htmlFor={`${element.attrs.id}-${recommendation.id}`}>
                      <RecommendationsSerializer
                        node={
                          (structureTiptapState?.content?.[0] ||
                            defaultRecommendationsStructure.content[0]) as JSONContent & SerializableNode
                        }
                        rootClassName={'h-full'}
                        rootStyle={{}}
                        recommendation={recommendation}
                        onChange={handleChange}
                        isChecked={isChecked || isSelectAllChecked}
                        isLive={isLive}
                        isSelectAllChecked={isSelectAllChecked}
                      />
                    </LabelWrapper>
                  </div>
                );
              }

              return (
                <div
                  key={recommendation.id}
                  style={
                    {
                      borderWidth: cardBorderWidth,
                      borderStyle: cardBorderStyle,
                      borderColor: cardBorderColor,
                      borderRadius: cardBorderRadius,
                      boxShadow: shadow,
                      background: backgroundColor,
                      padding: cardPadding,
                    } as CSSProperties
                  }
                  className=""
                >
                  <LabelWrapper isLive={isLive} htmlFor={`${element.attrs.id}-${recommendation.id}`}>
                    <Serializer
                      recommendation={recommendation}
                      attributes={element.attrs}
                      onChange={handleChange}
                      isChecked={isChecked}
                    />
                  </LabelWrapper>
                </div>
              );
            })}
          </GridWrapper>
          {!isPopupForm && <div>{formElement}</div>}
        </FormWrapper>
        {children}
      </div>
      {isPopupForm && (portalElement ? createPortal(popupElement, portalElement) : popupElement)}
    </>
  );
}
